import "./map.css";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  OverlayView,
  InfoWindow,
} from "@react-google-maps/api";
import { getAddressByLatLng, getFullAddressByLatLng } from "./MapFunction";
import { FaRegDotCircle } from "react-icons/fa";
import { AgGridReact } from "ag-grid-react";
import ProfileCard from "../ProfileCard/ProfileCard";
import {
  getInitials,
  getInitialColor,
  truncateString,
  CustomTooltip,
} from "../../services/commonServices";
import { useSelector } from "react-redux";

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

/* ── Coordinate guards ──────────────────────────────────────────
   Google Maps crashes with NaN. Every coord entering the map
   must pass isValidLatLng() before being used.
─────────────────────────────────────────────────────────────── */
const DEFAULT_CENTER = { lat: 12.985908334627606, lng: 77.58141163898391 };

const isFiniteNum = (v) => typeof v === "number" && Number.isFinite(v);

const isValidLatLng = (c) =>
  c != null && isFiniteNum(c.lat) && isFiniteNum(c.lng);

const safeFloat = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const darkModeStyle = [
  { elementType: "geometry",            stylers: [{ color: "#010113" }] },
  { elementType: "labels.icon",         stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill",    stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke",  stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry",         stylers: [{ color: "#757575" }] },
  { featureType: "poi",            elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road",           elementType: "geometry",         stylers: [{ color: "#383838" }] },
  { featureType: "water",          elementType: "geometry",         stylers: [{ color: "#000000" }] },
  { featureType: "water",          elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] },
];

const MarkerMap = ({
  centerPoint,
  markers = [],
  zoom,
  searchValue,
  width = "100%",
  height = "100%",
}) => {
  const themeStore = useSelector((state) => state.theme);

  const [isUserSelected,  setIsUserSelected]  = useState(false);
  /* CENTER always initialized to a valid value — never undefined/NaN */
  const [center,          setCenter]          = useState(
    () => isValidLatLng(centerPoint) ? centerPoint : DEFAULT_CENTER
  );
  const [zoomLevel,       setZoomLevel]       = useState(zoom || 10);
  const [map,             setMap]             = useState(null);
  const [hoveredMarker,   setHoveredMarker]   = useState(null);
  const [newMarkers,      setNewMarkers]      = useState([]);
  const [employee,        setEmployee]        = useState([]);
  const [isLocationFind,  setIsLocationFind]  = useState(false);
  const [userDetails,     setUserDetails]     = useState({});
  const [location,        setLocation]        = useState({});
  const [selectedKey,     setSelectedKey]     = useState(
    () => isValidLatLng(centerPoint) ? `${centerPoint.lat}-${centerPoint.lng}` : "default"
  );

  const containerStyle = { width, height };
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
  });

  /* ── build markers with addresses, skip any with invalid coords ── */
  useEffect(() => {
    if (!markers.length) return;

    const buildMarkers = async () => {
      const updated = await Promise.all(
        markers
          /* filter out bad coords before even trying geocode */
          .filter((m) => isFiniteNum(m.lat) && isFiniteNum(m.lng))
          .map(async (marker) => {
            let address = "N/A";
            try {
              /* getAddressByLatLng now also guards internally */
              address = await getAddressByLatLng(marker) || "N/A";
            } catch { /* ignore */ }
            return { ...marker, displayName: marker.name, address };
          })
      );
      setNewMarkers(updated);
    };

    buildMarkers();
    /* employee list starts as the full valid markers */
    setEmployee(markers.filter((m) => isFiniteNum(m.lat) && isFiniteNum(m.lng)));
  }, [markers]);

  /* ── fit bounds once markers are ready ── */
  useEffect(() => {
    if (mapRef.current && newMarkers.length > 0 && isLoaded) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
      mapRef.current.fitBounds(bounds, { padding: 50 });
    }
  }, [newMarkers, isLoaded]);

  /* ── keep center in sync with centerPoint prop ── */
  useEffect(() => {
    if (isUserSelected) return; // user has manually panned — don't override
    if (isValidLatLng(centerPoint)) {
      setCenter({ lat: centerPoint.lat, lng: centerPoint.lng });
    }
  }, [centerPoint, isUserSelected]);

  const onLoad = useCallback((mapInstance) => {
    mapRef.current = mapInstance;
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => { setMap(null); }, []);

  const isMarkerVisible = (marker) => {
    if (!map) return true;
    const bounds = map.getBounds();
    if (!bounds) return true;
    return bounds.contains(new window.google.maps.LatLng(marker.lat, marker.lng));
  };

  /* ── find location (pan to employee) ── */
  const againFind = (user) => {
    const lat = safeFloat(user.lat);
    const lng = safeFloat(user.lng);
    if (lat === null || lng === null) return;
    const pos = { lat, lng };
    mapRef.current?.panTo(pos);
    mapRef.current?.setZoom(24);
    setIsUserSelected(true);
    setCenter(pos);
    setZoomLevel(18);
    setSelectedKey(`${lat}-${lng}`);
  };

  const findLocation = async (user) => {
    const lat = safeFloat(user.lat);
    const lng = safeFloat(user.lng);
    if (lat === null || lng === null) {
      console.warn("[MarkerMap] findLocation — invalid coords:", user);
      return;
    }
    const pos = { lat, lng };
    setUserDetails(user.data);
    setLocation(await getFullAddressByLatLng(pos));
    mapRef.current?.panTo(pos);
    mapRef.current?.setZoom(18);
    setIsUserSelected(true);
    setCenter(pos);
    setZoomLevel(18);
    setTimeout(() => againFind(user), 100);
    setIsLocationFind(true);
  };

  /* ── search filter ── */
  useEffect(() => {
    if (!searchValue) {
      setEmployee(markers.filter((m) => isFiniteNum(m.lat) && isFiniteNum(m.lng)));
      return;
    }
    const q = searchValue.toLowerCase();
    setEmployee(
      markers.filter(
        (v) =>
          isFiniteNum(v.lat) && isFiniteNum(v.lng) &&
          (v.name?.toLowerCase().includes(q) ||
           v.employeeId?.toLowerCase().includes(q))
      )
    );
  }, [searchValue, markers]);

  /* ── mouse handlers ── */
  const handleMouseOver = (marker) => setHoveredMarker(marker);
  const handleMouseOut  = () => setHoveredMarker(null);

  /* ── column defs ── */
  const heading = useMemo(() => [
    {
      headerName: "Employee Name",
      pinned: "left",
      width: 225,
      flex: 1,
      tooltipField: "name",
      tooltipComponent: "customTooltip",
      cellRenderer: (rowData) => (
        <div className="d-flex align-items-center justify-content-start gap-2">
          <div style={{
            width: "30px", height: "30px",
            border: "1px solid lightgray", borderRadius: "50%",
            overflow: "hidden", display: "flex",
            alignItems: "center", justifyContent: "center",
            color: "white", fontWeight: "bold", fontSize: "12px",
            backgroundColor: rowData.data.staffImage ? "" : getInitialColor(rowData.data.name),
          }}>
            {rowData.data.staffImage ? (
              <img src={rowData.data.staffImage} style={{ width: "30px", height: "30px", borderRadius: "50%" }} alt="" />
            ) : getInitials(rowData.data.name)}
          </div>
          <span>{truncateString(rowData.data.name)}</span>
        </div>
      ),
    },
    {
      headerName: "Employee ID",
      cellRenderer: (rowData) => <span style={{ fontSize: "11px" }}>{rowData.data.employeeId}</span>,
      width: "115px",
    },
    {
      headerName: "",
      flex: 1,
      cellRenderer: (rowData) => (
        <button
          className="universal-btn"
          style={{ height: "25px", lineHeight: "5px", borderRadius: "7px", width: "95px", padding: "0 2px" }}
          onClick={() => findLocation({ lat: rowData.data.lat, lng: rowData.data.lng, data: rowData.data })}
        >
          <span className="font-size-10">Find Location</span>
        </button>
      ),
    },
  ], []);

  return isLoaded ? (
    <>
      {isLocationFind && (
        <div className="profile-card-container">
          <ProfileCard userDetails={userDetails} location={location} />
        </div>
      )}

      <div className="left-content p-2">
        <div className="ag-theme-quartz custom-grid" style={{ height: "calc(100vh - 200px)", width: "100%", marginTop: "5px" }}>
          <AgGridReact
            rowData={Array.isArray(employee) ? employee : []}
            columnDefs={heading}
            tooltipShowDelay={0}
            frameworkComponents={{ customTooltip: CustomTooltip }}
          />
        </div>
      </div>

      <div className="right-content">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}          /* always a valid {lat,lng} */
          zoom={zoomLevel}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            styles: themeStore.theme === "dark" ? darkModeStyle : null,
            disableDefaultUI: true,
            streetViewControl: false,
            mapTypeControl: false,
            zoomControl: true,
            zoomControlOptions: { position: 6 },
            fullscreenControl: true,
            fullscreenControlOptions: { position: 1 },
          }}
          onZoomChanged={() => { if (map) setZoomLevel(18); }}
        >
          {newMarkers.map((marker, index) => {
            /* Skip any marker that somehow still has bad coords */
            if (!isFiniteNum(marker.lat) || !isFiniteNum(marker.lng)) return null;

            const markerKey  = `${marker.lat}-${marker.lng}`;
            const isSelected = markerKey === selectedKey;

            return isMarkerVisible(marker) ? (
              <OverlayView
                key={index}
                position={{ lat: marker.lat, lng: marker.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  className={isSelected ? "custom-marker up-index" : "custom-marker"}
                  style={{
                    transform: "translate(-50%, -100%)",
                    borderRadius: "50%",
                    border:     isSelected ? "3px solid #00ff00" : "none",
                    boxShadow:  isSelected ? "0 0 10px #00ff00" : "none",
                    background: "linear-gradient(to bottom, lightgray 0%, gray 46%, white 46%, white 54%, #fc8702 54%, #fc8702 100%)",
                  }}
                  onMouseOver={() => handleMouseOver(marker)}
                  onMouseOut={handleMouseOut}
                >
                  <div className="marker-container">
                    <img
                      src={marker.staffImage || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTQEZrATmgHOi5ls0YCCQBTkocia_atSw0X-Q&s"}
                      alt="marker-img"
                      className="marker-img"
                    />
                  </div>
                  <div className="marker-pointer">
                    <FaRegDotCircle style={{ fontSize: "7px", position: "absolute", left: "-3px" }} />
                  </div>
                </div>
              </OverlayView>
            ) : null;
          })}

          {/* Hovered marker info window — only when coords are valid */}
          {hoveredMarker &&
            isFiniteNum(hoveredMarker.lat) &&
            isFiniteNum(hoveredMarker.lng) && (
            <InfoWindow
              position={{ lat: hoveredMarker.lat, lng: hoveredMarker.lng }}
              options={{ pixelOffset: new window.google.maps.Size(0, -50) }}
            >
              <div style={{ padding: "10px", maxWidth: "200px", borderRadius: "10px", backgroundColor: "#fff", boxShadow: "0 4px 8px rgba(0,0,0,0.2)" }}>
                <h4 style={{ fontSize: "16px", color: "#333", textAlign: "center" }}>{hoveredMarker.displayName}</h4>
                <p  style={{ margin: 0, fontSize: "14px", color: "#555", textAlign: "center" }}>{hoveredMarker.address}</p>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    </>
  ) : <></>;
};

export default React.memo(MarkerMap);